import type { CSSProperties, ReactNode } from "react";
import { Plus } from "lucide-react";
import type { SiteNode } from "@zirro/schema";
import { Catalog, FeaturedProducts } from "./Catalog";
import { slotKind, useStorefrontContext } from "./StorefrontContext";

/**
 * Fills store slot containers with live products. The wrapper keeps the
 * slot's node id so the editor can still select, move and style it.
 */
function SlotContent({ node }: { node: SiteNode }) {
  const { interactive, onEditProduct } = useStorefrontContext();
  const kind = slotKind(node);
  const columns = typeof node.props.columns === "number" ? node.props.columns : undefined;
  // A Section slot keeps a section's width and breathing room around the grid.
  const style: CSSProperties | undefined =
    node.type === "Section"
      ? { width: "calc(100% - 32px)", maxWidth: 1200, margin: "0 auto", padding: "16px 0 56px" }
      : undefined;
  return (
    <div data-node-id={node.id} className={interactive ? undefined : "zs-slot-preview"} style={style}>
      {!interactive ? (
        <span className="zs-slot-preview__label">
          {kind === "catalog" ? "Live catalog — your products, filters and sorting" : "Live products from your store"}
          {onEditProduct ? (
            <button
              type="button"
              className="zs-slot-preview__add"
              onClick={(e) => {
                e.stopPropagation();
                onEditProduct(null);
              }}
            >
              <Plus size={12} aria-hidden="true" />
              Add product
            </button>
          ) : null}
        </span>
      ) : null}
      {kind === "catalog" ? (
        <Catalog columns={columns} sidebar={node.props.direction === "horizontal"} />
      ) : (
        <FeaturedProducts limit={node.children?.length || 4} columns={columns} />
      )}
    </div>
  );
}

export function renderStoreSlot(node: SiteNode): ReactNode | undefined {
  return slotKind(node) ? <SlotContent node={node} /> : undefined;
}
